import {api} from './api';
import {Photo, PickerSessionResponse, PickerSessionStatus} from '../types';

export interface ConfirmSelectionResult {
  photos: Photo[];
  addedCount: number;
  skippedDueToLimit: number;
  skippedDueToDuplicate: number;
  maxPhotos: number;
}

export const photosPickerService = {
  /**
   * Creates a new picker session. Returns the session info including
   * the pickerUri that should be opened in a new tab/window.
   */
  async createSession(): Promise<PickerSessionResponse> {
    return api.post<PickerSessionResponse>('/api/v1/photos-picker/sessions', {});
  },

  /**
   * Gets the current status of a picker session.
   * Poll this until mediaItemsSet is true.
   */
  async getSessionStatus(sessionId: string): Promise<PickerSessionStatus> {
    return api.get<PickerSessionStatus>(
      `/api/v1/photos-picker/sessions/${sessionId}`,
    );
  },

  /**
   * Gets the count of selected media items in a picker session.
   * Call this after mediaItemsSet is true to know how many photos will be processed.
   */
  async getMediaItemsCount(sessionId: string): Promise<number> {
    const response = await api.get<{count: number}>(
      `/api/v1/photos-picker/sessions/${sessionId}/media-items`,
    );
    return response.count;
  },

  /**
   * Confirms the selection from a picker session and saves the photos to S3.
   * This ADDS to existing photos (does not replace them).
   * Returns info about how many were added vs skipped due to limit.
   */
  async confirmSelection(sessionId: string): Promise<ConfirmSelectionResult> {
    return api.post<ConfirmSelectionResult>(
      `/api/v1/photos-picker/sessions/${sessionId}/confirm`,
      {},
    );
  },

  /**
   * Parse the poll interval from ISO 8601 duration (e.g., "PT5S" -> 5000ms)
   */
  parsePollInterval(duration: string): number {
    // Simple parser for PT{n}S format
    const match = duration.match(/PT(\d+)S/);
    if (match) {
      return parseInt(match[1], 10) * 1000;
    }
    // Default to 5 seconds
    return 5000;
  },
};
